import React, { useEffect, useState, useRef, createElement } from 'react';
import { ChevronDown } from 'lucide-react';
interface RadarChartProps {
  scores: Record<string, Record<string, any>>;
  mainBrand: string;
  attributes: string[];
  allowMainBrandDeselection?: boolean;
}
const RadarChart: React.FC<RadarChartProps> = ({
  scores,
  mainBrand,
  attributes,
  allowMainBrandDeselection = false
}) => {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstance = useRef<any | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [selectedBrands, setSelectedBrands] = useState<string[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [chartJSLoaded, setChartJSLoaded] = useState(false);
  // Load Chart.js from CDN
  useEffect(() => {
    if (typeof window !== 'undefined') {
      // Check if Chart.js is already loaded
      if (window.Chart) {
        setChartJSLoaded(true);
        return;
      }
      // Load Chart.js from CDN
      const script = document.createElement('script');
      script.src = 'https://cdn.jsdelivr.net/npm/chart.js@4.4.1/dist/chart.umd.min.js';
      script.async = true;
      script.onload = () => {
        setChartJSLoaded(true);
      };
      document.body.appendChild(script);
      return () => {
        document.body.removeChild(script);
      };
    }
  }, []);
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEscKey);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscKey);
    };
  }, []);
  // Initialize with main brand and top competitors
  useEffect(() => {
    if (Object.keys(scores).length > 0) {
      // Calculate average scores for each brand
      const averageScores = Object.entries(scores).map(([brand, brandScores]) => {
        let sum = 0;
        let count = 0;
        Object.values(brandScores).forEach(score => {
          // Handle both old and new format
          const scoreValue = typeof score === 'number' ? score : score && typeof score === 'object' && 'score' in score ? score.score : 0;
          sum += scoreValue;
          count++;
        });
        const avg = count > 0 ? sum / count : 0;
        return {
          brand,
          score: avg
        };
      });
      // Sort by average score descending
      averageScores.sort((a, b) => b.score - a.score);
      // Always include the main brand, then add top competitors (up to 5 total)
      const topBrands = [mainBrand];
      averageScores.forEach(({
        brand
      }) => {
        if (brand !== mainBrand && topBrands.length < 5) {
          topBrands.push(brand);
        }
      });
      setSelectedBrands(topBrands);
    }
  }, [scores, mainBrand]);
  // Update chart when selections change
  useEffect(() => {
    if (!chartRef.current || selectedBrands.length === 0 || !chartJSLoaded || !window.Chart) return;
    // Destroy previous chart if it exists
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }
    // Prepare data for radar chart - only include selected brands
    const datasets = selectedBrands.map((brand, index) => {
      const isMainBrand = brand === mainBrand;
      const brandScores = scores[brand] || {};
      // Extract scores for each attribute (handling both old and new format)
      const dataPoints = attributes.map(attr => {
        const value = brandScores[attr];
        if (typeof value === 'number') {
          return value;
        } else if (typeof value === 'object' && value !== null && 'score' in value) {
          return value.score;
        }
        return 0;
      });
      // Generate colors - all brands get different colors
      const colorIndex = index;
      const colors = [{
        bg: 'rgba(22, 73, 255, 0.2)',
        border: 'rgb(22, 73, 255)'
      }, {
        bg: 'rgba(255, 99, 132, 0.2)',
        border: 'rgb(255, 99, 132)'
      }, {
        bg: 'rgba(75, 192, 192, 0.2)',
        border: 'rgb(75, 192, 192)'
      }, {
        bg: 'rgba(255, 159, 64, 0.2)',
        border: 'rgb(255, 159, 64)'
      }, {
        bg: 'rgba(153, 102, 255, 0.2)',
        border: 'rgb(153, 102, 255)'
      }, {
        bg: 'rgba(54, 162, 235, 0.2)',
        border: 'rgb(54, 162, 235)'
      }, {
        bg: 'rgba(255, 206, 86, 0.2)',
        border: 'rgb(255, 206, 86)'
      }, {
        bg: 'rgba(231, 233, 237, 0.2)',
        border: 'rgb(231, 233, 237)'
      }, {
        bg: 'rgba(97, 97, 97, 0.2)',
        border: 'rgb(97, 97, 97)'
      }, {
        bg: 'rgba(205, 220, 57, 0.2)',
        border: 'rgb(205, 220, 57)'
      }];
      const color = colors[colorIndex % colors.length];
      return {
        label: brand,
        data: dataPoints,
        backgroundColor: color.bg,
        borderColor: color.border,
        borderWidth: isMainBrand ? 2 : 1,
        pointBackgroundColor: color.border
      };
    });
    // Create chart
    const ctx = chartRef.current.getContext('2d');
    if (ctx) {
      chartInstance.current = new window.Chart(ctx, {
        type: 'radar',
        data: {
          labels: attributes,
          datasets
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          scales: {
            r: {
              min: 0,
              max: 100,
              ticks: {
                stepSize: 20
              },
              pointLabels: {
                font: {
                  size: 16
                }
              }
            }
          },
          plugins: {
            legend: {
              position: 'bottom',
              labels: {
                boxWidth: 12,
                padding: 15,
                font: {
                  size: 14
                }
              }
            },
            tooltip: {
              callbacks: {
                label: function (context) {
                  return `${context.dataset.label}: ${context.raw}`;
                }
              }
            }
          },
          elements: {
            line: {
              tension: 0.1
            }
          }
        }
      });
    }
    return () => {
      if (chartInstance.current) {
        chartInstance.current.destroy();
      }
    };
  }, [scores, mainBrand, attributes, selectedBrands, chartJSLoaded]);
  // Toggle brand selection
  const toggleBrand = (brand: string) => {
    if (selectedBrands.includes(brand)) {
      // Only allow deselecting the main brand if allowMainBrandDeselection is true
      if (brand === mainBrand && !allowMainBrandDeselection) return;
      // Don't allow deselecting the last brand
      if (selectedBrands.length <= 1) return;
      // Remove the brand
      setSelectedBrands(selectedBrands.filter(b => b !== brand));
    } else {
      // Add the brand if under limit
      if (selectedBrands.length < 10) {
        setSelectedBrands([...selectedBrands, brand]);
      }
    }
  };
  return <div className="w-full" data-id="element-784">
      <div className="flex justify-between items-center mb-4" data-id="element-785">
        <h3 className="font-medium text-gray-900" data-id="element-786">Radar Comparativo</h3>
        <div className="relative" ref={dropdownRef} data-id="element-787">
          <button onClick={() => setIsDropdownOpen(!isDropdownOpen)} className="px-3 py-1.5 bg-gray-100 text-gray-800 rounded-md hover:bg-gray-200 flex items-center text-sm" data-id="element-788">
            Marcas selecionadas ({selectedBrands.length})
            <ChevronDown size={14} className="ml-1" data-id="element-789" />
          </button>
          {isDropdownOpen && <div className="absolute right-0 mt-1 w-64 bg-white rounded-md shadow-lg z-10 border border-gray-200 max-h-80 overflow-y-auto" data-id="element-790">
              <div className="py-1" data-id="element-791">
                {Object.keys(scores).map(brand => <div key={brand} className="flex items-center px-4 py-2 hover:bg-gray-100" data-id="element-792">
                    <input type="checkbox" id={`brand-${brand}`} checked={selectedBrands.includes(brand)} onChange={() => toggleBrand(brand)} disabled={brand === mainBrand && !allowMainBrandDeselection && selectedBrands.length <= 1} className="mr-2 h-4 w-4 text-[#1649ff] focus:ring-[#1649ff] border-gray-300 rounded" data-id="element-793" />
                    <label htmlFor={`brand-${brand}`} className="text-sm text-gray-900" data-id="element-794">
                      {brand}
                    </label>
                  </div>)}
              </div>
            </div>}
        </div>
      </div>
      <div className="w-full h-[500px] flex items-center justify-center" data-id="element-795">
        {!chartJSLoaded ? <div className="text-center" data-id="element-796">
            <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-[#1649ff] mb-2" data-id="element-797"></div>
            <p data-id="element-798">Carregando gráfico...</p>
          </div> : <canvas ref={chartRef} data-id="element-799"></canvas>}
      </div>
      <div className="mt-2 text-xs text-gray-500 text-center" data-id="element-800">
        Selecione até 10 marcas para comparar no radar
      </div>
    </div>;
};
export default RadarChart;